import {
  useGetEmailTemplate,
  useUpdateEmailTemplate,
} from "@/queries/templates/email-templates.api";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import { Skeleton } from "@/components/ui/skeleton";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import EditEmailTemplateModal from "./EditEmailTemplateModal";
import TemplateEditor from "./TemplateEditor";
import type { SlotType } from "@/types/entities/templates";
import type { Editor } from "@tiptap/react";
import {
  //extractSlotsWithMetadata,
  transformSlotsToBackendFormat,
} from "../utils/slot-transformations";
import { useTemplateLoader } from "../hooks/useTemplateLoader";

interface DetalleEmailTemplateContentProps {
  templateId: string;
}

const DetalleEmailTemplateContent: React.FC<
  DetalleEmailTemplateContentProps
> = ({ templateId }) => {
  const { hasPermission } = useAuthorization();
  const canUpdateTemplate = hasPermission(
    Permissions["email-templates"].update,
  );

  const { data, isLoading, refetch } = useGetEmailTemplate(templateId);
  const { mutateAsync: updateTemplate, isPending: isSaving } =
    useUpdateEmailTemplate(templateId);
  const { showToast } = useToast();

  const [modalState, setModalState] = useState<"idle" | "edit-template">(
    "idle",
  );
  const [richTextContent, setRichTextContent] = useState("");
  const [initialContent, setInitialContent] = useState("");
  const [, setUsedSlots] = useState<Map<string, SlotType>>(new Map());
  const [emailSubject, setEmailSubject] = useState("");

  const editorRef = useRef<Editor | null>(null);

  useTemplateLoader({
    richText: data?.data.rich_text,
    onContentLoaded: (content) => {
      setRichTextContent(content);
      setInitialContent(content);
    },
    onSlotsExtracted: (slots) => {
      setUsedSlots(slots);
    },
    editorRef,
    templateType: "email",
  });

  const handleSlotsChange = useCallback((slots: Map<string, SlotType>) => {
    setUsedSlots(slots);
  }, []);

  const hasUnsavedChanges = richTextContent !== initialContent;

  useEffect(() => {
    if (data?.data?.subject) {
      setEmailSubject(data.data.subject);
    }
  }, [data?.data?.subject]);

  const handleSave = async () => {
    try {
      const transformedRichText =
        transformSlotsToBackendFormat(richTextContent);

      //const variables = extractSlotsWithMetadata(richTextContent); //TODO

      await updateTemplate({
        rich_text: transformedRichText,
        subject: emailSubject,
        // TODO: Agregar variables si el endpoint las acepta
        // variables: variables.map((variable) => ({
        //   ...variable,
        //   email_template_id: templateId,
        // })),
      });

      showToast({
        title: "Cambios guardados",
        description: "La plantilla se actualizó correctamente",
        options: {
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square">
                <Icon type="far" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });

      setInitialContent(richTextContent);
    } catch (error) {
      console.error("Error updating template:", error);
      showToast({
        title: "Error al guardar",
        description: "No se pudieron guardar los cambios. Inténtalo de nuevo.",
        options: {
          duration: 5000,
          type: "error",
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full flex flex-col h-full overflow-hidden p-4 gap-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="w-full flex flex-col items-center justify-center h-full">
        <Heading variant="h5" className="text-heading-quiet">
          Plantilla no encontrada
        </Heading>
      </div>
    );
  }

  return (
    <>
      {modalState === "edit-template" && (
        <EditEmailTemplateModal
          templateData={{
            id: data.data.id,
            name: data.data.name,
            description: data.data.description || "",
          }}
          open={modalState === "edit-template"}
          onSuccess={() => {
            refetch();
            setModalState("idle");
          }}
          onClose={() => {
            setModalState("idle");
          }}
          onDeactivate={() => {
            setModalState("idle");
          }}
        />
      )}

      <div className="border border-neutral shadow-custom rounded-xl flex flex-col h-full overflow-hidden">
        <div className="flex justify-between items-start gap-3 border-b border-neutral w-full p-3">
          <div className="flex justify-start gap-2.5 items-center flex-auto truncate">
            {canUpdateTemplate && (
              <Button
                size="default"
                variant="quiet"
                onClick={() => {
                  setModalState("edit-template");
                }}
              >
                <Icon type="far" name="edit" className="text-current" />
              </Button>
            )}
            {isLoading ? (
              <div className="flex flex-col">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-3 w-64" />
              </div>
            ) : (
              <div className="flex flex-col justify-center max-h-10 truncate w-full">
                <Heading
                  variant="h6"
                  className="text-body-loud w-full truncate"
                  title={data?.data?.name || "Nombre de la plantilla"}
                >
                  {data?.data?.name || "Nombre de la plantilla"}
                </Heading>
                <p
                  className="text-body-quiet -translate-y-[3px] text-sm w-full truncate"
                  title={data?.data?.description || ""}
                >
                  {data?.data?.description || ""}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 h-10">
            {canUpdateTemplate && (
              <Button
                variant="default"
                size="xs"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
              >
                Guardar <Icon type="far" name="save" className="text-current" />
                {isSaving && (
                  <Icon
                    type="fas"
                    name="spinner-third"
                    className="animate-spin"
                  />
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <TemplateEditor
            value={richTextContent}
            onChange={setRichTextContent}
            placeholder="Escribe el contenido del correo aquí..."
            minHeight="calc(100vh - 300px)"
            maxHeight="calc(100vh - 300px)"
            showSlots={true}
            showHeadings={true}
            showAlignment={true}
            showIndent={false}
            integrated={true}
            templateType="email"
            subjectValue={emailSubject}
            onSubjectChange={setEmailSubject}
            onSlotsChange={handleSlotsChange}
            editorRef={editorRef}
          />
        </div>
      </div>
    </>
  );
};

export default DetalleEmailTemplateContent;
